import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import IconButton from "@mui/material/IconButton";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";

import { GlobalState } from "./GlobalContext";
import * as DmsService from '../supports/dms-service'


function createId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function stripThinkBlocks(text) {
  return text.replace(/<think>[\s\S]*?(<\/think>|$)/gi, "");
}

function transformIdLinks(text, reference) {
  if (!text) return text;

  // Deduplicate consecutive identical file IDs (e.g. [ID:1][ID:1][ID:1] -> [ID:1])
  const cleanedText = text.replace(/\[ID:(\d+)\](?:\s*\[ID:\1\])+/gi, "[ID:$1]");

  const sequenceRegex = /\[ID:\d+\](?:\s*[,;]?\s*\[ID:\d+\])*/gi;
  return cleanedText.replace(sequenceRegex, (match) => {
    // Extract all rawIds from the matched sequence
    const ids = [];
    const idRegex = /\[ID:(\d+)\]/gi;
    let idMatch;
    while ((idMatch = idRegex.exec(match)) !== null) {
      ids.push(idMatch[1]);
    }

    const uniqueOutputs = [];
    const seenKeys = new Set();

    for (const rawId of ids) {
      const refItem = Array.isArray(reference)
        ? reference.find((item) => item && String(item.id) === String(rawId))
        : null;

      if (!refItem) {
        // Hide unresolved references from the UI
        continue;
      }

      // display file name
      const displayName =
        refItem.file_name ||
        refItem.document_id ||
        `ID:${rawId}`;

      const docId = refItem.dms_doc_id || "";
      const rendered = docId
        ? `[${displayName}](#doc-${encodeURIComponent(docId)})`
        : displayName;

      const key = refItem.document_id || rawId;
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        uniqueOutputs.push(rendered);
      }
    }

    return uniqueOutputs.join(" ");
  });
}


function extractAnswerAndReference(rawText) {
  if (!rawText) {
    return { answer: "", reference: [] };
  }

  let answer = rawText;
  let reference = [];

  const marker = "reference:";
  const markerIndex = rawText.lastIndexOf(marker);

  if (markerIndex >= 0) {
    answer = rawText.slice(0, markerIndex);

    const refText = rawText.slice(markerIndex + marker.length).trim();

    try {
      const parsedRef = JSON.parse(refText);
      if (Array.isArray(parsedRef)) {
        reference = parsedRef;
      }
    } catch (_) {
      reference = [];
    }
  }

  return {
    answer: stripThinkBlocks(answer).trim(),
    reference,
  };
}


function renderAiMarkdown(text, reference, onDocLinkClick) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => (
          <Typography variant="body2" component="p" sx={{ m: 0, mb: 1 }}>
            {children}
          </Typography>
        ),
        ul: ({ children }) => (
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {children}
          </Box>
        ),
        ol: ({ children }) => (
          <Box component="ol" sx={{ m: 0, pl: 3 }}>
            {children}
          </Box>
        ),
        li: ({ children }) => (
          <Box component="li" sx={{ mb: 0.5 }}>
            <Typography variant="body2" component="span">
              {children}
            </Typography>
          </Box>
        ),
        a: ({ href, children }) => {
          if (href && href.startsWith("#doc-")) {
            const docId = decodeURIComponent(href.replace("#doc-", ""));

            return (
              <>&nbsp;
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onDocLinkClick && onDocLinkClick(docId);
                  }}
                  style={{
                    color: "#1976d2",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  {children}
                </a>
                &nbsp;
              </>

            );
          }

          return (
            <>&nbsp;
              <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
              &nbsp;
            </>
          );
        },
        code: ({ inline, children, ...props }) => {
          if (inline) {
            return (
              <Box
                component="code"
                sx={{
                  px: 0.5,
                  py: 0.2,
                  borderRadius: 0.5,
                  bgcolor: "action.hover",
                  fontFamily: "monospace",
                  fontSize: "0.9em",
                }}
              >
                <code {...props}>{children}</code>
              </Box>
            );
          }

          return (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1,
                overflow: "auto",
                borderRadius: 1,
                bgcolor: "#f5f5f5",
                fontFamily: "monospace",
                fontSize: 12,
              }}
            >
              <code {...props}>{children}</code>
            </Box>
          );
        },
      }}
    >
      {transformIdLinks(text || "", reference)}
    </ReactMarkdown>
  );
}

export default function RagMessagingDialog({
  title,
  placeholder,
  initialMessage,
  height,
  sysID,
  userID,
  // folderID,
  folder,
  folders,
  // resetToken,
  onDocLinkClick,
}) {
  const RAG_SERVICE_UNAVAILABLE_MSG =
    "RAG service is unavailable. Please refresh or retry later.";


  const [messages, setMessages] = useState([
    {
      id: createId(),
      role: "ai",
      text: initialMessage,
      reference: [],
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [error, setError] = useState("");
  const [chatEnabled, setChatEnabled] = useState(false);
  const [checkingFiles, setCheckingFiles] = useState(false);
  const [initializingChat, setInitializingChat] = useState(false);
  const [datasetHint, setDatasetHint] = useState("Please select a folder first.");

  const [readyFileCount, setReadyFileCount] = useState(0);
  const [totalFileCount, setTotalFileCount] = useState(0);
  const [fileChunking, setFileChunking] = useState(false);

  const chatBottomRef = useRef(null);
  const abortRef = useRef(null);

  const [gState] = React.useContext(GlobalState);

  const [ragServiceUnavailable, setRagServiceUnavailable] = useState(false);

  const compactMode = gState.useCompactMode || false;

  const folderID = folder?.id || 0;

  const dataset =
    sysID && folderID ? `${String(sysID)}-${String(folderID)}` : "";

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const appendMessage = (message) => {
    setMessages((prev) => [...prev, message]);
  };

  const replaceMessageById = (id, newMessage) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? newMessage : m)));
  };

  const conversionFolderIDs = [folderID];

  // const vFolder = folders.find((f) => f.id === folderID);
  // console.log("Current folder:", vFolder);

  if (folder) {
    folders.forEach((f) => {
      if (f.FolderPath?.startsWith(folder.FolderPath) && f.RagEnabled && f.id !== folderID) {
        conversionFolderIDs.push(f.id);
      }
    });
  }


  const [resettingDataset, setResettingDataset] = useState(false);
  // const [filePollingToken, setFilePollingToken] = useState(0);



  // Polling for ragFiles is disabled as requested
  // useEffect(() => {
  //   let cancelled = false;
  //   let timer = null;
  //
  //   const poll = async () => {
  //     // console.log('Polling file ready status...', { sysID, folderID, vConversionFolderIDs });
  //     try {
  //       const allReady = await refreshFileReadyStatus();
  //
  //       if (cancelled) return;
  //
  //       if (!allReady) {
  //         if (!ragServiceUnavailable) {
  //           setDatasetHint(
  //             "Updating the knowledge base with new files now. I'm still available if you have any questions!"
  //           );
  //         }
  //
  //         timer = setTimeout(poll, 5000);
  //       } else {
  //         if (!ragServiceUnavailable) {
  //           setDatasetHint("");
  //         }
  //       }
  //     } catch (_) {
  //       if (!cancelled) {
  //         timer = setTimeout(poll, 5000);
  //       }
  //     }
  //   };
  //
  //   if (folderID && sysID) {
  //     poll();
  //   }
  //
  //   return () => {
  //     cancelled = true;
  //     if (timer) clearTimeout(timer);
  //   };
  // }, [folder, filePollingToken, ragServiceUnavailable]);

  const refreshFileReadyStatus = async () => {
    if (ragServiceUnavailable) {
      return false;
    }

    // console.log('Checking file ready status...', { sysID, folderID, vConversionFolderIDs });
    if (!sysID || !folderID || conversionFolderIDs.length === 0) {
      setReadyFileCount(0);
      setTotalFileCount(0);
      setFileChunking(false);
      return true;
    }

    const [newFileChunking, datasetsData] = await DmsService.ragFilesPromise(
      gState.sessionID,
      sysID,
      conversionFolderIDs
    );

    const files = [];

    (datasetsData || []).forEach((dataset) => {
      (dataset.files || []).forEach((file) => {
        if (file?.name !== "blank.txt") {
          files.push(file);
        }
      });
    });

    const readyCount = files.filter(
      (file) => String(file?.status || "").toLowerCase().startsWith("ready")
    ).length;

    setReadyFileCount(readyCount);
    setTotalFileCount(files.length);
    setFileChunking(newFileChunking === true);

    return files.length > 0 && readyCount === files.length && newFileChunking !== true;
  };



  const handleResetDatasetClick = async () => {
    if (conversionFolderIDs.length === 0 || resettingDataset) return;

    setResettingDataset(true);
    setError("");
    setDatasetHint("Resetting dataset...");

    try {
      await DmsService.ragResetDatasetPromise(
        gState.sessionID,
        sysID,
        conversionFolderIDs,
      );

      setReadyFileCount(0);
      setTotalFileCount(0);
      setFileChunking(true);

      setMessages([
        {
          id: createId(),
          role: "thinking",
          text: "Initializing chat...",
          reference: [],
        },
      ]);

      setInput("");
      setConversationId(null);
      setChatEnabled(false);
      setInitializingChat(true);

      const newConversationId = await ragInitChatWithRetry(10, 1000);

      setConversationId(newConversationId);
      setChatEnabled(true);
      setDatasetHint("");

      setMessages([
        {
          id: createId(),
          role: "ai",
          text: initialMessage,
          reference: [],
        },
      ]);

    } catch (_) {
      setChatEnabled(false);
      setConversationId(null);
      setRagServiceUnavailable(true);
      setError(RAG_SERVICE_UNAVAILABLE_MSG);
      setDatasetHint(RAG_SERVICE_UNAVAILABLE_MSG);
      setMessages([
        {
          id: createId(),
          role: "ai",
          text: RAG_SERVICE_UNAVAILABLE_MSG,
          reference: [],
        },
      ]);
    } finally {
      setResettingDataset(false);
      setInitializingChat(false);
    }
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const ragInitChatWithRetry = async (maxRetries = 10, intervalMs = 1000, checkCancelled = () => false) => {
    let lastError = null;

    for (let i = 0; i < maxRetries; i += 1) {
      if (checkCancelled()) return null;
      try {
        const newConversationId = await DmsService.ragInitChatPromise(
          gState.sessionID,
          sysID,
          conversionFolderIDs
        );

        if (newConversationId) {
          return newConversationId;
        }

        lastError = new Error("conversationID was not returned by initial_chat API.");
      } catch (err) {
        lastError = err;
      }

      if (checkCancelled()) return null;

      if (i < maxRetries - 1) {
        await sleep(intervalMs);
      }
    }

    throw new Error(RAG_SERVICE_UNAVAILABLE_MSG);
  };

  useEffect(() => {
    let cancelled = false;

    const prepareChat = async () => {
      setRagServiceUnavailable(false);
      setConversationId(null);
      setChatEnabled(false);
      setError("");

      if (folderID === 0) {
        setMessages([
          {
            id: createId(),
            role: "ai",
            text: initialMessage,
            reference: [],
          },
        ]);
        setDatasetHint("Please select a folder first.");
        return;
      }

      setMessages([
        {
          id: createId(),
          role: "thinking",
          text: "Initializing chat...",
          reference: [],
        },
      ]);

      setCheckingFiles(false);
      setInitializingChat(true);
      setDatasetHint("");

      try {
        const newConversationId = await ragInitChatWithRetry(10, 1000, () => cancelled);

        if (cancelled) return;

        setConversationId(newConversationId);
        setChatEnabled(true);

        const allReady = await refreshFileReadyStatus();

        if (cancelled) return;

        if (!allReady) {
          setDatasetHint(
            "Updating the knowledge base with new files now. I'm still available if you have any questions!"
          );
        } else {
          setDatasetHint("");
        }

        setMessages([
          {
            id: createId(),
            role: "ai",
            text: initialMessage,
            reference: [],
          },
        ]);
      } catch (err) {
        if (cancelled) return;
        setChatEnabled(false);
        setConversationId(null);
        setRagServiceUnavailable(true);
        setDatasetHint(RAG_SERVICE_UNAVAILABLE_MSG);
        setMessages([
          {
            id: createId(),
            role: "ai",
            text: RAG_SERVICE_UNAVAILABLE_MSG,
            reference: [],
          },
        ]);
      } finally {
        if (!cancelled) {
          setCheckingFiles(false);
          setInitializingChat(false);
        }
      }
    };

    prepareChat();

    return () => {
      cancelled = true;
    };
  }, [folder, folderID]);

  const handleSend = async () => {
    const message = input.trim();
    if (!message || sending) return;

    if (!chatEnabled || !conversationId) {
      appendMessage({
        id: createId(),
        role: "ai",
        text: "Please upload files to this folder and wait for chat initialization before chatting.",
        reference: [],
      });
      return;
    }

    setError("");

    const userMessage = {
      id: createId(),
      role: "user",
      text: message,
    };

    const thinkingId = createId();
    const thinkingMessage = {
      id: thinkingId,
      role: "thinking",
      text: "Thinking...",
      reference: [],
    };

    appendMessage(userMessage);
    appendMessage(thinkingMessage);
    setInput("");
    setSending(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await DmsService.ragCompletionsStreamPromise(
        gState.sessionID,
        conversationId,
        message,
        sysID,
        conversionFolderIDs,
        controller.signal
      );

      if (!response) {
        throw new Error("No response received from completions API.");
      }

      if (!response.ok) {
        let errText = `Server Error: ${response.status}`;
        try {
          const errData = await response.json();
          errText = errData.detail || errData.message || errData.errMsg || errText;
        } catch (_) { }
        throw new Error(errText);
      }

      if (!response.body) {
        throw new Error("Response body is empty.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let fullAnswer = "";
      let latestReference = [];
      let buffer = "";

      replaceMessageById(thinkingId, {
        id: thinkingId,
        role: "ai",
        text: "",
        reference: [],
      });

      while (true) {
        const { value, done } = await reader.read();

        if (value) {
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split(/\r?\n/);
          buffer = lines.pop() || "";

          lines.forEach((line) => {
            const trimmedLine = line.trim();

            if (!trimmedLine.startsWith("data:")) return;

            const dataStr = trimmedLine.replace(/^data:\s*/, "");

            if (!dataStr || dataStr === "[DONE]") return;

            try {
              const data = JSON.parse(dataStr);

              if (data.answer) {
                fullAnswer += data.answer;
              }

              if (Array.isArray(data.reference)) {
                latestReference = data.reference;
              }

              if (data.conversationID || data.conversation_id) {
                setConversationId(data.conversationID || data.conversation_id);
              }

              replaceMessageById(thinkingId, {
                id: thinkingId,
                role: "ai",
                text: stripThinkBlocks(fullAnswer),
                reference: latestReference,
              });
            } catch (_) {
              // ignore incomplete/invalid JSON line
            }
          });
        }

        if (done) break;
      }

      // handle leftover buffer
      const leftover = buffer.trim();
      if (leftover.startsWith("data:")) {
        const dataStr = leftover.replace(/^data:\s*/, "");

        if (dataStr && dataStr !== "[DONE]") {
          try {
            const data = JSON.parse(dataStr);

            if (data.answer) {
              fullAnswer += data.answer;
            }

            if (Array.isArray(data.reference)) {
              latestReference = data.reference;
            }

            if (data.conversationID || data.conversation_id) {
              setConversationId(data.conversationID || data.conversation_id);
            }
          } catch (_) { }
        }
      }

      replaceMessageById(thinkingId, {
        id: thinkingId,
        role: "ai",
        text: stripThinkBlocks(fullAnswer) || "No response received.",
        reference: latestReference,
      });
    } catch (err) {
      if (err.name === "AbortError") return;

      replaceMessageById(thinkingId, {
        id: thinkingId,
        role: "ai",
        text: `Sorry, request failed: ${err.message}`,
        reference: [],
      });
      setError(err.message || "Unknown error");
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const inputDisabled =
    sending ||
    checkingFiles ||
    resettingDataset ||
    initializingChat ||
    ragServiceUnavailable ||
    // fileChunking ||
    !chatEnabled ||
    !conversationId;

  // console.log("inputDisabled", inputDisabled, {
  //   sending,
  //   checkingFiles,
  //   resettingDataset,
  //   initializingChat,
  //   // fileChunking,
  //   chatEnabled,
  //   conversationId
  // });

  const effectivePlaceholder = checkingFiles
    ? "Checking files for this folder..."
    : initializingChat
      ? "Initializing chat..."
      : chatEnabled
        ? placeholder
        : "Please upload files to this folder before chatting.";

  return (
    <Paper
      variant="outlined"
      sx={{
        height,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "background.paper",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>

        {/* Invisible for now, might be used again later */}
        {false && (
          <Button
            size="small"
            variant="outlined"
            disabled={!folderID || checkingFiles || initializingChat || sending || resettingDataset}
            onClick={handleResetDatasetClick}
            sx={{
              fontSize: compactMode ? 11 : 12,
              px: compactMode ? 1 : 1.5,
              py: compactMode ? 0.25 : 0.5,
              minWidth: "auto",
              whiteSpace: "nowrap",
            }}
          >
            {resettingDataset ? "Resetting..." : "Reset Dataset"}
          </Button>
        )}
      </Box>

      {ragServiceUnavailable && datasetHint && (
        <Box sx={{ px: 2, pt: 1.25, flexShrink: 0 }}>
          <Alert severity="error">
            {datasetHint}
          </Alert>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 2,
          py: 2,
          bgcolor: "#fafafa",
        }}
      >
        <Stack spacing={2}>
          {messages.map((msg) => {
            const isUser = msg.role === "user";
            const isThinking = msg.role === "thinking";

            return (
              <Box
                key={msg.id}
                sx={{
                  display: "flex",
                  justifyContent: isUser ? "flex-end" : "flex-start",
                }}
              >
                <Stack
                  direction={isUser ? "row-reverse" : "row"}
                  spacing={1}
                  sx={{
                    maxWidth: "80%",
                    alignItems: "flex-start",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 30,
                      height: 30,
                      bgcolor: isUser ? "primary.main" : "grey.300",
                      color: isUser ? "primary.contrastText" : "text.primary",
                    }}
                  >
                    {isUser ? (
                      <PersonOutlineIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
                    )}
                  </Avatar>

                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.5,
                      py: 1.1,
                      borderRadius: 2,
                      bgcolor: isUser ? "primary.main" : "common.white",
                      color: isUser ? "primary.contrastText" : "text.primary",
                      border: isUser ? "none" : "1px solid",
                      borderColor: "divider",
                      whiteSpace: isUser ? "pre-wrap" : "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {isThinking ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={14} thickness={5} />
                        <Typography variant="body2">{msg.text}</Typography>
                      </Stack>
                    ) : isUser ? (
                      <Typography variant="body2">{msg.text}</Typography>
                    ) : (
                      <Box sx={{ fontSize: 14 }}>
                        {renderAiMarkdown(
                          msg.text,
                          msg.reference,
                          onDocLinkClick
                        )}
                      </Box>
                    )}
                  </Paper>
                </Stack>
              </Box>
            );
          })}
          <div ref={chatBottomRef} />
        </Stack>
      </Box>

      {error ? (
        <Box sx={{ px: 2, py: 0.5, flexShrink: 0 }}>
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        </Box>
      ) : null}

      <Box
        sx={{
          p: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={effectivePlaceholder}
            disabled={inputDisabled}
            sx={{
              "& .MuiInputBase-input": {
                fontSize: 13,
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={inputDisabled || !input.trim()}
            sx={{
              width: compactMode ? 36 : 42,
              height: compactMode ? 36 : 42,
              bgcolor: "primary.main",
              color: "white",
              "&:hover": {
                bgcolor: "primary.dark",
              },
              "&.Mui-disabled": {
                bgcolor: "action.disabledBackground",
                color: "action.disabled",
              },
            }}
          >
            <ArrowUpwardOutlinedIcon
              sx={{ fontSize: compactMode ? 18 : 22 }}
            />
          </IconButton>
        </Stack>
      </Box>
    </Paper>
  );
}

RagMessagingDialog.propTypes = {
  title: PropTypes.string,
  placeholder: PropTypes.string,
  initialMessage: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sysID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  userID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  // folderID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  folder: PropTypes.object,
  folders: PropTypes.array,
  // resetToken: PropTypes.number,
  onDocLinkClick: PropTypes.func,
};

RagMessagingDialog.defaultProps = {
  title: "AI Bot",
  placeholder: "Type your question here...",
  initialMessage: "Hello! How can I help you today?",
  height: "100%",
  // folderID: "",
  folder: null,
  folders: [],
  // resetToken: 0,
  onDocLinkClick: null,
};